/**
 * Document Management Actions Component
 * Provides role-based action menus for document management
 * - Admin: Full access (download, delete)
 * - Staff: Limited access (download only)
 */

import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Button } from '../ui/button';
import { isAdmin } from '../../utils/permissions';

const DELETE_DOCUMENT = gql`
  mutation DeleteDocument($id: ID!) {
    deleteDocument(id: $id)
  }
`;

const RESTORE_DOCUMENT = gql`
  mutation RestoreDocument($id: ID!) {
    restoreDocument(id: $id)
  }
`;

const PERMANENTLY_DELETE_DOCUMENT = gql`
  mutation PermanentlyDeleteDocument($id: ID!) {
    permanentlyDeleteDocument(id: $id)
  }
`;

interface DocumentManagementActionsProps {
  documentId: string;
  documentName: string;
  userRole?: string;
  isDeleted?: boolean;
  onDownload: () => void;
  onRefresh?: () => void;
}

export function DocumentManagementActions({
  documentId,
  documentName,
  userRole,
  isDeleted = false,
  onDownload,
  onRefresh,
}: DocumentManagementActionsProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const buttonRef = useRef<HTMLDivElement>(null);

  const [deleteDocument, { loading: deleting }] = useMutation(DELETE_DOCUMENT);
  const [restoreDocument, { loading: restoring }] = useMutation(RESTORE_DOCUMENT);
  const [permanentlyDeleteDocument, { loading: permanentlyDeleting }] = useMutation(PERMANENTLY_DELETE_DOCUMENT);

  const isAdminUser = isAdmin(userRole as any);

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If less than 200px space below and more space above, open upward
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setMenuPosition('top');
      } else {
        setMenuPosition('bottom');
      }
    }
  }, [showMenu]);

  const handleDeleteDocument = async () => {
    if (!confirm(`Are you sure you want to delete "${documentName}"? This will hide the document but it can be restored later.`)) {
      return;
    }

    try {
      await deleteDocument({ variables: { id: documentId } });
      alert(`Document "${documentName}" has been deleted.`);
      onRefresh?.();
      setShowMenu(false);
    } catch (error: any) {
      console.error('Error deleting document:', error);
      alert(`Error deleting document: ${error.message}`);
    }
  };

  const handleRestoreDocument = async () => {
    if (!confirm(`Restore "${documentName}"? This will make the document visible again.`)) {
      return;
    }

    try {
      await restoreDocument({ variables: { id: documentId } });
      alert(`Document "${documentName}" has been restored.`);
      onRefresh?.();
      setShowMenu(false);
    } catch (error: any) {
      console.error('Error restoring document:', error);
      alert(`Error restoring document: ${error.message}`);
    }
  };

  const handlePermanentlyDeleteDocument = async () => {
    if (!confirm(`⚠️ PERMANENTLY DELETE "${documentName}"?\n\nThis will:\n• Delete the file from S3 storage\n• Remove the database record\n• This action CANNOT be undone\n\nAre you absolutely sure?`)) {
      return;
    }

    // Double confirmation for permanent delete
    if (!confirm(`Final confirmation: Type YES in your mind and click OK to permanently delete "${documentName}". This is your last chance to cancel.`)) {
      return;
    }

    try {
      await permanentlyDeleteDocument({ variables: { id: documentId } });
      alert(`Document "${documentName}" has been permanently deleted.`);
      onRefresh?.();
      setShowMenu(false);
    } catch (error: any) {
      console.error('Error permanently deleting document:', error);
      alert(`Error permanently deleting document: ${error.message}`);
    }
  };

  return (
    <div className="relative" ref={buttonRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}
        className="text-gray-600 hover:text-aalaw-red"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </Button>

      {showMenu && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowMenu(false)}
          />
          <div className={`absolute right-0 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 ${
            menuPosition === 'top' ? 'bottom-full mb-2' : 'mt-2'
          }`}>
            <div className="py-1">
              {/* Download - Available to all staff */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload();
                  setShowMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>

              {/* Admin-only actions */}
              {isAdminUser && (
                <>
                  <div className="border-t border-gray-200 my-1" />
                  
                  {isDeleted ? (
                    <>
                      {/* Restore Document */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRestoreDocument();
                        }}
                        disabled={restoring}
                        className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {restoring ? 'Restoring...' : 'Restore Document'}
                      </button>

                      {/* Permanently Delete Document */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePermanentlyDeleteDocument();
                        }}
                        disabled={permanentlyDeleting}
                        className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-100 flex items-center gap-2 disabled:opacity-50 font-semibold"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        {permanentlyDeleting ? 'Deleting...' : 'Delete Permanently'}
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Soft Delete Document */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDocument();
                        }}
                        disabled={deleting}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {deleting ? 'Deleting...' : 'Delete Document'}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
